import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Camera, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Proof() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [reg, setReg] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/registrations/${id}`);
      setReg(res.data);
      setState("ready");
    } catch {
      setState("notfound");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error(t.proof.errorType);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/registrations/${id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      setFile(null);
      setPreview(null);
      toast.success(t.proof.successTitle);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.proof.errorGeneric));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased" data-testid="proof-page">
      <div className="border-b spec-border bg-white px-4 sm:px-6 py-4">
        <Link to="/" data-testid="proof-logo" className="flex items-center w-fit">
          <Logo markClass="w-6 h-6 text-blue-600" textClass="text-lg" />
        </Link>
      </div>

      <div className="max-w-xl mx-auto px-4 py-12">
        {state === "loading" && (
          <div className="text-center py-20 text-slate-500 font-mono-spec text-sm">
            <Loader2 className="animate-spin mx-auto mb-3" size={24} />
          </div>
        )}

        {state === "notfound" && (
          <div className="bg-white border spec-border p-8 rounded-sm text-center" data-testid="proof-notfound">
            <p className="text-sm text-slate-600">{t.proof.notFound}</p>
          </div>
        )}

        {state === "ready" && reg && (
          <div className="space-y-6">
            <div>
              <span className="text-xs font-mono-spec text-gold-600 font-semibold tracking-wider uppercase">{t.proof.eyebrow}</span>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 mt-2" data-testid="proof-title">
                {t.proof.hi} {reg.first_name} — {t.proof.title}
              </h1>
              <p className="text-sm text-slate-600 mt-2">{t.proof.intro}</p>
            </div>

            <div className="bg-white border spec-border p-5 rounded-sm grid grid-cols-2 gap-4 font-mono-spec text-xs">
              <div>
                <div className="text-slate-400">{t.proof.device}</div>
                <div className="text-slate-900 font-bold mt-1" data-testid="proof-device">{reg.device_model}</div>
              </div>
              <div>
                <div className="text-slate-400">{t.proof.statusLabel}</div>
                <div className="text-slate-900 font-bold mt-1" data-testid="proof-status">{reg.status}</div>
              </div>
            </div>

            {reg.proof_uploaded ? (
              <div className="bg-white border spec-border p-6 rounded-sm text-center space-y-4" data-testid="proof-already">
                <h2 className="text-lg font-bold text-slate-950">{t.proof.alreadyTitle}</h2>
                <span
                  className={`inline-block px-3 py-1.5 text-xs font-mono-spec rounded-sm ${
                    reg.proof_verified
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                      : "bg-gold-50 border border-gold-200 text-gold-700"
                  }`}
                  data-testid="proof-badge"
                >
                  {reg.proof_verified ? t.proof.verifiedBadge : t.proof.pendingBadge}
                </span>
                <div className="border spec-border rounded-sm overflow-hidden">
                  <img
                    src={`${BACKEND}/api/files/${reg.proof_path}?reg=${id}`}
                    alt="Installation proof"
                    data-testid="proof-image"
                    className="w-full object-cover max-h-96"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white border spec-border p-6 rounded-sm space-y-4" data-testid="proof-upload-box">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  capture="environment"
                  className="hidden"
                  data-testid="proof-file-input"
                  onChange={pickFile}
                />
                {preview ? (
                  <div className="border spec-border rounded-sm overflow-hidden">
                    <img src={preview} alt="Preview" data-testid="proof-preview" className="w-full object-cover max-h-96" />
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="proof-pick-btn"
                    onClick={() => fileInput.current?.click()}
                    className="w-full border-2 border-dashed border-slate-300 rounded-sm py-12 flex flex-col items-center gap-3 text-slate-500 hover:border-gold-600 hover:text-gold-600 transition-colors"
                  >
                    <Camera size={28} />
                    <span className="text-xs font-mono-spec font-medium">{t.proof.uploadLabel}</span>
                    <span className="text-[10px] font-mono-spec text-slate-400">{t.proof.uploadHint}</span>
                  </button>
                )}
                <div className="flex items-center justify-between gap-3">
                  {preview && (
                    <button
                      type="button"
                      data-testid="proof-change-btn"
                      onClick={() => fileInput.current?.click()}
                      className="text-xs font-medium text-slate-600 hover:text-slate-950"
                    >
                      {t.proof.changePhoto}
                    </button>
                  )}
                  <button
                    data-testid="proof-submit-btn"
                    onClick={upload}
                    disabled={!file || uploading}
                    className="ml-auto px-6 py-3 bg-slate-950 text-white font-mono-spec text-xs font-bold rounded-sm hover:bg-gold-800 transition-colors disabled:opacity-40"
                  >
                    {uploading ? t.proof.uploading : t.proof.submit}
                  </button>
                </div>
              </div>
            )}

            {reg.proof_uploaded && reg.proof_verified && (
              <div className="bg-emerald-50 border border-emerald-200 p-5 rounded-sm flex items-start gap-3" data-testid="proof-success">
                <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="font-bold text-sm text-emerald-900">{t.proof.successTitle}</div>
                  <p className="text-xs text-emerald-800 mt-1">{t.proof.successSub}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
